"use strict";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result) __defProp(target, key, result);
  return result;
};
/**
 * @license
 * Copyright 2016 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { debounce } from "lodash-es";
import { LayerChunkProgressInfo } from "#src/chunk_manager/base.js";
import { RenderViewport, renderViewportsEqual } from "#src/display_context.js";
import {
  ProjectionParameters,
  projectionParametersEqual
} from "#src/projection_parameters.js";
import { get3dModelToDisplaySpaceMatrix } from "#src/render_coordinate_transform.js";
import {
  PROJECTION_PARAMETERS_CHANGED_RPC_METHOD_ID,
  PROJECTION_PARAMETERS_RPC_ID
} from "#src/render_layer_common.js";
import { WatchableSet } from "#src/trackable_value.js";
import { RefCounted } from "#src/util/disposable.js";
import { mat4 } from "#src/util/geom.js";
import { MessageList, MessageSeverity } from "#src/util/message_list.js";
import { NullarySignal, Signal } from "#src/util/signal.js";
import { VisibilityPriorityAggregator } from "#src/visibility_priority/frontend.js";
import { registerSharedObjectOwner, SharedObject } from "#src/worker_rpc.js";
export var RenderLayerRole = /* @__PURE__ */ ((RenderLayerRole2) => {
  RenderLayerRole2[RenderLayerRole2["DATA"] = 0] = "DATA";
  RenderLayerRole2[RenderLayerRole2["ANNOTATION"] = 1] = "ANNOTATION";
  RenderLayerRole2[RenderLayerRole2["DEFAULT_ANNOTATION"] = 2] = "DEFAULT_ANNOTATION";
  return RenderLayerRole2;
})(RenderLayerRole || {});
export function allRenderLayerRoles() {
  return new WatchableSet([
    0 /* DATA */,
    1 /* ANNOTATION */,
    2 /* DEFAULT_ANNOTATION */
  ]);
}
export class RenderLayer extends RefCounted {
  constructor() {
    super(...arguments);
    this.role = 0 /* DATA */;
    this.messages = new MessageList();
    this.layerChanged = new NullarySignal();
    this.redrawNeeded = new NullarySignal();
    this.layerChunkProgressInfo = new LayerChunkProgressInfo();
  }
  handleAction(_action) {
  }
  getValueAt(_x) {
    return void 0;
  }
  /**
   * Transform the stored pickedValue and offset associated with the retrieved pick ID into the
   * actual value.
   */
  transformPickedValue(pickState) {
    return pickState.pickedValue;
  }
  /**
   * Optionally updates the mouse state based on the retrived pick information.  This might snap the
   * 3-d position to the center of the picked point.
   */
  updateMouseState(_mouseState, _pickedValue, _pickedOffset, _data) {
  }
}
export class VisibilityTrackedRenderLayer extends RenderLayer {
  constructor() {
    super(...arguments);
    this.visibility = new VisibilityPriorityAggregator();
  }
  attach(attachment) {
    attachment;
  }
}
export function update3dRenderLayerAttachment(transform, displayDimensionRenderInfo, attachment) {
  let { state } = attachment;
  if (state === void 0 || state.transform !== transform || state.displayDimensionRenderInfo !== displayDimensionRenderInfo) {
    attachment.messages.clearMessages();
    state = attachment.state = {
      transform,
      displayDimensionRenderInfo,
      modelTransform: void 0
    };
    if (transform.error !== void 0) {
      attachment.messages.addMessage({
        severity: MessageSeverity.error,
        message: transform.error
      });
      return void 0;
    }
    try {
      const modelTransform = mat4.create();
      get3dModelToDisplaySpaceMatrix(
        modelTransform,
        displayDimensionRenderInfo,
        transform
      );
      state.modelTransform = modelTransform;
    } catch (e) {
      attachment.messages.addMessage({
        severity: MessageSeverity.error,
        message: e.message
      });
    }
  }
  return state.modelTransform;
}
export class DerivedProjectionParameters extends RefCounted {
  constructor(options) {
    super();
    this.renderViewport = new RenderViewport();
    this.changed = new Signal();
    const {
      parametersConstructor = ProjectionParameters,
      navigationState,
      update,
      isEqual = projectionParametersEqual
    } = options;
    this.oldValue_ = new parametersConstructor();
    this.value_ = new parametersConstructor();
    const performUpdate = () => {
      const { oldValue_, value_ } = this;
      oldValue_.displayDimensionRenderInfo = navigationState.displayDimensionRenderInfo.value;
      Object.assign(oldValue_, this.renderViewport);
      let { globalPosition } = oldValue_;
      const newGlobalPosition = navigationState.position.value;
      const rank = newGlobalPosition.length;
      if (globalPosition.length !== rank) {
        oldValue_.globalPosition = globalPosition = new Float32Array(rank);
      }
      globalPosition.set(newGlobalPosition);
      update(oldValue_, navigationState);
      if (isEqual(oldValue_, value_)) return;
      this.value_ = oldValue_;
      this.oldValue_ = value_;
      this.changed.dispatch(value_, oldValue_);
    };
    const debouncedUpdate = this.update = this.registerCancellable(
      debounce(performUpdate, 0)
    );
    this.registerDisposer(navigationState.changed.add(debouncedUpdate));
    performUpdate();
  }
  setViewport(viewport) {
    if (renderViewportsEqual(viewport, this.renderViewport)) return;
    Object.assign(this.renderViewport, viewport);
    this.update();
  }
  get value() {
    this.update.flush();
    return this.value_;
  }
}
export let SharedProjectionParameters = class extends SharedObject {
  constructor(rpc, base, updateInterval = 10) {
    super();
    this.base = base;
    this.updateInterval = updateInterval;
    this.prevDisplayDimensionRenderInfo = void 0;
    this.update = this.registerCancellable(
      debounce((_oldValue, newValue) => {
        let valueUpdate;
        if (newValue.displayDimensionRenderInfo !== this.prevDisplayDimensionRenderInfo) {
          valueUpdate = newValue;
          this.prevDisplayDimensionRenderInfo = newValue.displayDimensionRenderInfo;
        } else {
          const { displayDimensionRenderInfo, ...remainder } = newValue;
          valueUpdate = remainder;
        }
        this.rpc.invoke(PROJECTION_PARAMETERS_CHANGED_RPC_METHOD_ID, {
          id: this.rpcId,
          value: valueUpdate
        });
      }, this.updateInterval)
    );
    this.initializeCounterpart(rpc, { value: base.value });
    this.registerDisposer(base.changed.add(this.update));
  }
  flush() {
    this.update.flush();
  }
};
SharedProjectionParameters = __decorateClass([
  registerSharedObjectOwner(PROJECTION_PARAMETERS_RPC_ID)
], SharedProjectionParameters);
